import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ShieldCheck, Trash2, Save, Plus, Search, X } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { useAuthStore } from '../store/authStore';
import { usuariosApi } from '../api/backend';
import { Usuario } from '../types';
import { useToast } from '../hooks/useToast';

const roleOptions: { value: Usuario['papel']; label: string }[] = [
  { value: 'operador', label: 'Analista' },
  { value: 'aprovador', label: 'Gestor' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Super Admin' },
];

const initialForm = {
  nome: '',
  email: '',
  telefone: '',
  senha: '',
  papel: 'operador' as Usuario['papel'],
};

type FormState = typeof initialForm;

export function UsuariosPage() {
  const { isAdminRole } = useAuthStore();
  const navigate = useNavigate();
  const toast = useToast();

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormState>(initialForm);
  const [roleChanges, setRoleChanges] = useState<Record<number, Usuario['papel']>>({});
  const [savingRoleId, setSavingRoleId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usuariosApi.list();
      setUsuarios(data);
    } catch (error) {
      console.error('Erro ao carregar usuários', error);
      toast.error('Erro ao carregar usuários', 'Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdminRole()) {
      navigate('/dashboard');
      return;
    }

    loadUsuarios();
  }, [isAdminRole, navigate]);

  const filteredUsuarios = useMemo(() => {
    if (!searchTerm) return usuarios;

    const term = searchTerm.toLowerCase();
    return usuarios.filter((user) =>
      user.nome.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term) ||
      (user.telefone?.includes(term) ?? false)
    );
  }, [usuarios, searchTerm]);

  const handleFormChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateUser = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.nome || !formData.email || !formData.senha) {
      toast.error('Preencha os campos obrigatórios', 'Nome, email e senha são obrigatórios.');
      return;
    }

    try {
      setSubmitting(true);
      await usuariosApi.create(formData);
      toast.success('Usuário criado', `${formData.nome} foi adicionado.`);
      setFormData({ ...initialForm });
      setIsModalOpen(false);
      await loadUsuarios();
    } catch (error) {
      console.error('Erro ao criar usuário', error);
      toast.error('Falha ao criar usuário', 'Verifique os dados e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleUpdate = async (userId: number) => {
    const papel = roleChanges[userId];
    if (!papel || savingRoleId === userId) return;

    try {
      setSavingRoleId(userId);
      await usuariosApi.updateRole(userId, papel);
      toast.success('Papel atualizado', 'As permissões do usuário foram salvas.');
      setRoleChanges((prev) => {
        const clone = { ...prev };
        delete clone[userId];
        return clone;
      });
      await loadUsuarios();
    } catch (error) {
      console.error('Erro ao atualizar papel', error);
      toast.error('Não foi possível salvar', 'Tente novamente.');
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const user = usuarios.find((u) => u.id === userId);
    if (!user) return;

    const confirmDelete = window.confirm(`Excluir ${user.nome}? Essa ação não pode ser desfeita.`);
    if (!confirmDelete) return;

    try {
      setDeletingId(userId);
      await usuariosApi.delete(userId);
      toast.success('Usuário removido', `${user.nome} foi excluído.`);
      await loadUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário', error);
      toast.error('Falha ao excluir usuário', 'Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!isAdminRole()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <BackButton to="/dashboard" />
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Users className="w-8 h-8 text-primary-600" />
              Gerenciar Usuários
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Administre acesso da equipe: cadastre novos usuários, ajuste papéis e remova contas quando necessário.
            </p>
          </div>
          <button
            onClick={() => {
              setFormData({ ...initialForm });
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 rounded-lg px-4 py-2.5 bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Novo Usuário
          </button>
        </div>

        <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nome, email ou telefone"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {loading ? (
              <div className="py-16 flex flex-col items-center text-gray-500">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
                <p className="mt-4">Carregando usuários...</p>
              </div>
            ) : filteredUsuarios.length === 0 ? (
              <div className="py-14 text-center text-gray-500">
                <ShieldCheck className="w-12 h-12 mx-auto mb-3" />
                <p>Nenhum usuário encontrado com os filtros atuais.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nome</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Telefone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Papel</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredUsuarios.map((user) => {
                      const selectedRole = roleChanges[user.id] ?? user.papel;
                      const roleDirty = selectedRole !== user.papel;
                      return (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900 dark:text-white">{user.nome}</div>
                            <div className="text-xs text-gray-500">ID #{user.id}</div>
                          </td>
                          <td className="px-4 py-4 text-gray-700 dark:text-gray-200">{user.email}</td>
                          <td className="px-4 py-4 text-gray-700 dark:text-gray-200">{user.telefone || '--'}</td>
                          <td className="px-4 py-4">
                            <select
                              value={selectedRole}
                              onChange={(event) => {
                                const value = event.target.value as Usuario['papel'];
                                setRoleChanges((prev) => {
                                  const next = { ...prev };
                                  if (value === user.papel) {
                                    delete next[user.id];
                                  } else {
                                    next[user.id] = value;
                                  }
                                  return next;
                                });
                              }}
                              className={`w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-900 ${
                                roleDirty
                                  ? 'border-primary-500 text-primary-600'
                                  : 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200'
                              }`}
                            >
                              {roleOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleRoleUpdate(user.id)}
                                disabled={!roleDirty || savingRoleId === user.id}
                                className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                  roleDirty
                                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                }`}
                              >
                                <Save className="w-4 h-4" />
                                Salvar
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={deletingId === user.id}
                                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg border border-gray-200 dark:border-gray-700 p-6 relative">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200">
                <Plus className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Novo Usuário</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Preencha os dados para liberar acesso.</p>
              </div>
            </div>

            <form className="space-y-4" onSubmit={handleCreateUser}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(event) => handleFormChange('nome', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(event) => handleFormChange('email', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Telefone</label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(event) => handleFormChange('telefone', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Senha *</label>
                <input
                  type="password"
                  value={formData.senha}
                  onChange={(event) => handleFormChange('senha', event.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Papel *</label>
                <select
                  value={formData.papel}
                  onChange={(event) => handleFormChange('papel', event.target.value as FormState['papel'])}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2"
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60"
                >
                  <Plus className="w-4 h-4" />
                  Criar Usuário
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
